/** The envelope ids a cue path has already taken, bounded so a long mount never grows a leak. */

/** Envelope ids remembered for the dedupe window. Older ids fall out in arrival order. */
export const CUE_DEDUPE_WINDOW = 256;

export interface CueMemory {
  /** Whether this envelope id is already remembered. */
  saw: (envelopeId: string) => boolean;
  /** Remember it, dropping the oldest once the window is full. */
  remember: (envelopeId: string) => void;
  /** Forget everything. The caller's own counters are cumulative and survive. */
  clear: () => void;
}

/**
 * A bounded set of envelope ids.
 *
 * `saw` and `remember` are two steps rather than one, because the transport remembers only what it
 * actually queued: a shed cue is never remembered, so its redelivery may still play.
 */
export function createCueMemory(limit: number = CUE_DEDUPE_WINDOW): CueMemory {
  const seen = new Set<string>();

  return {
    saw: (envelopeId) => seen.has(envelopeId),

    remember: (envelopeId) => {
      seen.add(envelopeId);
      if (seen.size <= limit) return;
      const oldest = seen.values().next();
      if (oldest.done !== true) seen.delete(oldest.value);
    },

    clear: () => {
      seen.clear();
    },
  };
}
